import { useState, useCallback } from 'react';
import { useTonConnectUI } from '@tonconnect/ui-react';
import { Address, beginCell } from '@ton/core';
import { CONTRACT_ADDRESS, DEPOSIT_AMOUNT } from '../config';
import { useGameStore } from '../stores/gameStore';
import type { TransactionResult } from '../types';

interface UseDepositReturn {
  sendDeposit: (message?: string, referrer?: string) => Promise<TransactionResult>;
  isLoading: boolean;
  error: string | null;
  reset: () => void;
}

export const useDeposit = (): UseDepositReturn => {
  const [tonConnectUI] = useTonConnectUI();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    setError(null);
    setIsLoading(false);
  }, []);

  const sendDeposit = useCallback(async (message?: string, referrer?: string): Promise<TransactionResult> => {
    if (!tonConnectUI.wallet) {
      setError('Wallet not connected');
      return { success: false, error: 'WALLET_NOT_CONNECTED', timestamp: Date.now() };
    }
    if (!CONTRACT_ADDRESS) {
      setError('Contract address is not configured');
      return { success: false, error: 'CONTRACT_ERROR', timestamp: Date.now() };
    }

    setIsLoading(true);
    setError(null);

    try {
      const payloadCell = referrer
        ? beginCell().storeUint(0x7b000010, 32).storeAddress(Address.parse(referrer)).endCell()
        : beginCell().storeUint(0, 32).storeStringTail(message ?? '').endCell();
      const payload = payloadCell.toBoc().toString('base64');

      const result = await tonConnectUI.sendTransaction({
        validUntil: Math.floor(Date.now() / 1000) + 300,
        messages: [
          {
            address: CONTRACT_ADDRESS,
            amount: DEPOSIT_AMOUNT.toString(),
            payload,
          },
        ],
      });

      setTimeout(() => useGameStore.getState().fetchRoundInfo(), 3000);
      setIsLoading(false);

      return {
        success: true,
        tx_hash: result.boc,
        amount: DEPOSIT_AMOUNT,
        timestamp: Date.now(),
      };
    } catch (err) {
      console.error('Deposit transaction failed:', err);
      let errorCode = 'TRANSACTION_FAILED';
      let errorMessage = 'Transaction failed';
      if (err instanceof Error) {
        const m = err.message.toLowerCase();
        if (m.includes('reject') || m.includes('cancel')) {
          errorCode = 'USER_CANCELLED';
          errorMessage = 'Transaction cancelled';
        } else if (m.includes('insufficient')) {
          errorCode = 'INSUFFICIENT_BALANCE';
          errorMessage = 'Insufficient balance';
        } else {
          errorMessage = err.message;
        }
      }
      setError(errorMessage);
      setIsLoading(false);
      return { success: false, error: errorCode, timestamp: Date.now() };
    }
  }, [tonConnectUI]);

  return { sendDeposit, isLoading, error, reset };
};
